import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { newClientRef } from '../../core/client-ref';
import { api } from '../../core/client';
import { submitQueueable } from '../../offline-queue';
import type {
  TransferOrder,
  TransferOrderCreate,
  TransferOrderDetail,
  TransferOrderStatus,
  TransferStageTarget,
  TransferTransition,
} from './transfer-orders.types';

const BASE = '/v1/itsm/transfer-orders';
const KEY = ['transfer-orders'] as const;

export function useTransferOrders(status?: TransferOrderStatus | '' | undefined) {
  return useQuery({
    queryKey: [...KEY, { status: status || null }],
    queryFn: () => api.get<TransferOrder[]>(`${BASE}${status ? `?status=${status}` : ''}`),
    refetchInterval: 30_000,
  });
}

export function useTransferOrder(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<TransferOrderDetail>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useCreateTransferOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransferOrderCreate) => api.post<TransferOrder>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Advance one line's stage (picked/staged/delivered). Offline-queued and
 *  idempotent via `client_ref` — a reconnect replay of an already-applied
 *  stage 409s and is dropped. The key is generated ONCE here so the queued
 *  entry and any retry carry the same ref. */
export function useStageTransferLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      lineId,
      stage,
      client_ref,
    }: {
      id: string;
      lineId: string;
      stage: TransferStageTarget;
      client_ref?: string;
    }) =>
      submitQueueable<boolean>({
        method: 'POST',
        path: `${BASE}/${id}/lines/${lineId}/stage`,
        body: { stage, client_ref: client_ref ?? newClientRef() },
        invalidate: [...KEY],
        label: `Stage ${stage}`,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** TO status move. On `received`, the backend drives each line's asset lifecycle
 *  move through the gated path (409 on a missing photo/location/signature). */
export function useTransitionTransferOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TransferTransition }) =>
      api.post<TransferOrder>(`${BASE}/${id}/transition`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
